import {
    Component,
    OnInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    ElementRef,
    ViewChild,
    ChangeDetectorRef,
    Input,
    Output,
    EventEmitter,
} from '@angular/core';
import * as THREE from 'three';
import * as OBC from '@thatopen/components';
import * as OBF from '@thatopen/components-front';
import * as BUI from '@thatopen/ui';
import { BuildingPlan, PlanStatus } from '../project-building-plans/building-plan.model';
import { AttachmentService } from '../../../core/attachment/attachment.service';
import { UtilsService } from '../../../core/utils/utils.service';
import { SettingsService } from '../../../settings/settings.service';
import { NotificationService } from '../../../core/notification/notification.service';
import { DataRecordingSetting, ServiceGroup } from '../../../service-groups/service-group.model';
import { take } from 'rxjs/operators';
import { LoggerService } from '../../../core/logger/logger.service';
import { ProjectService } from '../../project.service';
import { TranslateService } from '@ngx-translate/core';
import { ActionSheetController, PopoverController } from '@ionic/angular';
import { BimData, JobMarker, Job } from '../project-jobs/job.model';
import { DbService } from '../../../core/db/db.service';
import { ServiceGroupListComponent } from '../project-building-plans/building-plan-details/service-group-list/service-group-list.component';
import { Project } from '../../project.model';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { BimBuildingPlansService } from './bim-building-plans.service';

@Component({
    selector: 'app-project-bim-building-plans',
    templateUrl: './project-bim-building-plans.component.html',
    styleUrls: ['./project-bim-building-plans.component.scss'],
})
export class ProjectBimBuildingPlansComponent implements OnInit, OnDestroy, OnChanges {
    @Input() buildingPlan: BuildingPlan;
    @Input() buildingPlanList: BuildingPlan[];
    @Input() job: Job;
    @Input() viewOnly = false;
    @Input() projectView = false;
    @Input() asDialog = false;
    @Input() subView = false; // set when opening this component from the task view or the job linkings (sub view in modal)
    @Output() destructiveClicked = new EventEmitter();
    @Output() dataChanged = new EventEmitter<boolean>();
    @Output() successClicked = new EventEmitter<boolean>();
    @Output() jobShouldShow = new EventEmitter<{ job: Job; servicegroup: ServiceGroup; project: Project }>();
    @Output() planChanged = new EventEmitter<string>();
    @Output() markerChanged = new EventEmitter<Job>();
    @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLDivElement>;

    // helper
    activeSegment: 'map' | 'job-list' = 'map';
    markerMode = false;
    addingNewItem = false;
    addingNewMarkerToJob = false;
    loading = false;
    modelLoaded = false;
    selectedFileName = '';
    runningExport = false;
    showBackdrop = false;
    allMeasurements: { unit: string; values: number[]; sum: number } | null = null;

    // Import panel state
    selectedImportType: string | null = null;
    selectedImportServiceGroup: ServiceGroup | null = null;
    importElementCount = 0;
    generatingJobs = false;

    // IFC Type Visibility
    ifcTypes: { name: string; visible: boolean }[] = [];
    hider!: OBC.Hider;
    classifier!: OBC.Classifier;

    // Popup
    popupVisible = false;
    popupX = 0;
    popupY = 0;
    currentJobId = '';
    currentKey = '';
    displayMarkerId = '';
    displayJob: Job;
    displayServiceGroup: ServiceGroup;
    displayProject: Project;
    activePopover: 'import' | 'objectTypes' | 'measurement' | null = null;
    activePopoverEvent: Event | undefined;

    // Three.js / IFC Scene
    private components!: OBC.Components;
    private world!: OBC.World & { scene: OBC.SimpleScene; camera: OBC.OrthoPerspectiveCamera; renderer: OBF.RendererWith2D };
    private fragments!: OBC.FragmentsManager;
    private viewpoints!: OBC.Viewpoints;
    private loadedModel: any;
    private loadedModelId: string | null = null;
    lengthMeasurer!: OBF.LengthMeasurement;
    areaMeasurer!: OBF.AreaMeasurement;
    measurementMode: 'none' | 'length' | 'area' = 'none';

    private markerHeadMesh!: THREE.InstancedMesh;
    private markerNeedleMesh!: THREE.InstancedMesh;
    private markerHeadGeo!: THREE.BufferGeometry;
    private markerNeedleGeo!: THREE.BufferGeometry;
    private markerData = new Map<number, string>(); // instanceIndex → jobId
    private jobToInstance = new Map<string, number>(); // jobId → instanceIndex
    private markerLabels = new Map<string, THREE.Sprite>(); // jobId → label sprite
    private markerCount = 0;
    private markerCapacity = 200; // grows by 200 when full

    // Reusable THREE objects
    private readonly raycaster = new THREE.Raycaster();
    private readonly rayMouse = new THREE.Vector2();
    private readonly threeMatrix = new THREE.Matrix4();
    private readonly threeColor = new THREE.Color();
    private readonly threeVec3 = new THREE.Vector3();

    protected projectServiceGroups: Array<ServiceGroup> = [];
    allowMultipleMarker = false;

    private keydownListener!: (e: KeyboardEvent) => void;
    private cameraUpdateListener!: () => void;
    private markerMoveListener!: (e: MouseEvent) => void;
    private markerClickListener!: (e: MouseEvent) => void;
    private markingClickListener!: (e: MouseEvent) => void;
    private bpReloading = false;
    private _pendingReload = false;
    private _destroyed = false;

    // Unsaved changes tracking
    private _unsavedChanges = false;
    private planDataChanged = false;
    private changedMarkers = new Map<string, { color?: string; markerName?: string }>();

    /** Plan is still being converted (frag file not available yet) */
    get planPreparing(): boolean {
        return !!this.buildingPlan && !this.buildingPlan.attachment?.filePath && this.buildingPlan.dataStatus !== PlanStatus.Failed;
    }

    /** Plan conversion failed */
    get planFailed(): boolean {
        return this.buildingPlan?.dataStatus === PlanStatus.Failed;
    }

    set unsavedChanges(value: boolean) {
        this._unsavedChanges = value;
        this.dataChanged.emit(this._unsavedChanges);
    }

    constructor(
        private cdr: ChangeDetectorRef,
        private attachmentSrv: AttachmentService,
        protected utils: UtilsService,
        protected settingsSrv: SettingsService,
        private notifySrv: NotificationService,
        private logger: LoggerService,
        private projectSvc: ProjectService,
        protected translate: TranslateService,
        private actionSheetController: ActionSheetController,
        private popoverCtrl: PopoverController,
        private db: DbService,
        private bimSrv: BimBuildingPlansService
    ) {}

    async ngOnInit() {
        BUI.Manager.init();
        await this.initScene();
        await this.loadFragmentsFromS3();
        this.initMarkerMesh();
        this.initMarkerClick();
        this.initKeyboardControls();
        this.initMarking();
        this.initMeasurements();
        const zoomedToMarker = await this.loadExistingMarkers();
        if (!zoomedToMarker) this.fitModel();

        this.projectSvc.currentProjectServiceGroupList$.pipe(take(1)).subscribe((serviceGroups) => {
            this.logger.log('[BIM BUILDING PLAN] Loaded project service group list');
            this.projectServiceGroups = serviceGroups;
            const foundSg = this.projectServiceGroups.find((sg) => sg._id === this.job?.serviceGroupId);
            if (foundSg) this.allowMultipleMarker = foundSg.settings.dataRecording.multipleLocationMarker;
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.buildingPlan && !changes.buildingPlan.firstChange) {
            const oldId = changes.buildingPlan.previousValue?._id;
            const newId = changes.buildingPlan.currentValue?._id;
            if (oldId !== newId) this.reloadPlan();
        }
    }

    ngOnDestroy() {
        this._destroyed = true;
        this._pendingReload = false;
        this.world?.camera?.controls?.removeEventListener('update', this.cameraUpdateListener);
        this.containerRef.nativeElement.removeEventListener('mousemove', this.markerMoveListener);
        this.containerRef.nativeElement.removeEventListener('click', this.markerClickListener);
        this.containerRef.nativeElement.removeEventListener('click', this.markingClickListener);
        for (const label of this.markerLabels.values()) {
            this.world?.scene?.three?.remove(label);
            try {
                (label.material as THREE.SpriteMaterial).map?.dispose();
                label.material.dispose();
            } catch (_) {
                // already disposed after scene rebuild
            }
        }
        this.markerLabels.clear();
        this.disposeMarkerMesh(this.markerHeadMesh);
        this.disposeMarkerMesh(this.markerNeedleMesh);
        this.markerHeadGeo?.dispose();
        this.markerNeedleGeo?.dispose();
        window.removeEventListener('keydown', this.keydownListener);

        this.teardownScene();
    }

    /**
     * Disposes model, components and renderer in this exact order: model first (async worker
     * round-trip that also terminates the worker thread), then components, WebGL context last.
     * Tearing the renderer down first interrupts the async fragments dispose and leaks the
     * worker + model memory, which accumulates until iOS kills the WKWebView.
     */
    private async teardownScene() {
        const modelId = this.loadedModelId;
        const fragments = this.fragments;
        const components = this.components;
        const renderer = this.world?.renderer?.three;
        this.loadedModel = null;
        this.loadedModelId = null;
        try {
            if (modelId && fragments?.initialized) await fragments.core.disposeModel(modelId);
        } catch (_) {}
        try {
            components?.dispose();
        } catch (_) {}
        try {
            if (renderer) {
                renderer.dispose();
                renderer.forceContextLoss();
                // Shrink the canvas before removal: iOS keeps the canvas backing store
                // alive until GC, this releases it immediately.
                renderer.domElement.width = 1;
                renderer.domElement.height = 1;
                renderer.domElement?.remove();
            }
        } catch (_) {}
    }

    /**
     * Init the bim building-plan
     */
    async initScene() {
        this.components = new OBC.Components();
        this.viewpoints = this.components.get(OBC.Viewpoints);

        const worlds = this.components.get(OBC.Worlds);
        this.world = worlds.create<OBC.SimpleScene, OBC.OrthoPerspectiveCamera, OBF.RendererWith2D>();

        this.world.scene = new OBC.SimpleScene(this.components);
        (this.world.scene as OBC.SimpleScene).setup();
        this.world.scene.three.background = null;

        // No MSAA on phones: antialiasing multiplies framebuffer memory and iOS kills the
        // WKWebView process when GPU + JS memory exceed its limit.
        const rendererParams = this.utils.isPhone ? { antialias: false } : undefined;
        this.world.renderer = new OBF.RendererWith2D(this.components, this.containerRef.nativeElement, rendererParams);
        this.world.renderer.showLogo = false;
        this.world.camera = new OBC.OrthoPerspectiveCamera(this.components);
        await this.world.camera.controls.setLookAt(78, 20, -2.2, 26, -4, 25);

        // Cap the device pixel ratio: iPhones report DPR 3, which means ~4x the GPU memory of DPR 1.5.
        // Large frag models + DPR 3 push the WKWebView over its memory limit and iOS kills the process (app "crash").
        const maxPixelRatio = this.utils.isPhone ? 1.5 : 2;
        this.world.renderer.three.setPixelRatio(Math.min(window.devicePixelRatio, maxPixelRatio));
        this.world.renderer.resize();

        this.components.init();
        this.initFragments();
        this.hider = this.components.get(OBC.Hider);
        this.classifier = this.components.get(OBC.Classifier);
    }

    initMeasurements() {
        this.areaMeasurer = this.components.get(OBF.AreaMeasurement);
        this.areaMeasurer.world = this.world;
        this.areaMeasurer.color = new THREE.Color('#2092c9');
        this.areaMeasurer.mode = 'face';

        this.lengthMeasurer = this.components.get(OBF.LengthMeasurement);
        this.lengthMeasurer.world = this.world;
        this.lengthMeasurer.color = new THREE.Color('#e87a00');
        this.lengthMeasurer.mode = 'free';
        this.lengthMeasurer.delay = 0;
        this.lengthMeasurer.enabled = false;
    }

    toggleLengthMeasurement() {
        if (this.measurementMode === 'length') {
            this.measurementMode = 'none';
            this.lengthMeasurer.enabled = false;
            this.lengthMeasurer.list.clear();
            this.allMeasurements = null;
        } else {
            this.measurementMode = 'length';
            this.lengthMeasurer.enabled = true;
            this.areaMeasurer.enabled = false;
            this.areaMeasurer.list.clear();
        }
        this.cdr.detectChanges();
    }

    toggleAreaMeasurement() {
        if (this.measurementMode === 'area') {
            this.measurementMode = 'none';
            this.areaMeasurer.enabled = false;
            this.areaMeasurer.list.clear();
            this.allMeasurements = null;
        } else {
            this.measurementMode = 'area';
            this.areaMeasurer.enabled = true;
            this.lengthMeasurer.enabled = false;
            this.lengthMeasurer.list.clear();
        }
        this.cdr.detectChanges();
    }

    /**
     * Initializes the FragmentsManager and registers listeners that adds newly loaded model triggers rerender.
     */
    private initFragments() {
        const workerUrl = 'assets/fragments/worker.mjs';
        this.fragments = this.components.get(OBC.FragmentsManager);
        this.fragments.init(workerUrl);

        // 0 = lowest quality (library default). Keep it pinned on phones so a future default change
        // can't silently increase memory usage on iOS.
        if (this.utils.isPhone) this.fragments.core.settings.graphicsQuality = 0;

        this.cameraUpdateListener = () => {
            if (this.fragments?.initialized) this.fragments.core.update();
        };
        this.world.camera.controls.addEventListener('update', this.cameraUpdateListener);

        this.fragments.list.onItemSet.add(({ value: model }) => {
            model.useCamera(this.world.camera.three);
            this.world.scene.three.add(model.object);
            this.fragments.core.update(true);
            this.modelLoaded = true;
        });

        this.fragments.core.models.materials.list.onItemSet.add(({ value: material }) => {
            if (!('isLodMaterial' in material && material.isLodMaterial)) {
                material.polygonOffset = true;
                material.polygonOffsetUnits = 1;
                material.polygonOffsetFactor = Math.random();
            }
        });
    }

    /**
     * Downloads the pre-converted .frag file from S3, loads it into the FragmentsManager, classifies all IFC types,
     * and hides camera-marker proxies (IfcBuildingElementProxy).
     */
    private async loadFragmentsFromS3() {
        if (!this.buildingPlan?.attachment?.filePath) return;

        const planId = this.buildingPlan._id;
        this.loading = true;
        this.selectedFileName = this.buildingPlan.attachment.displayName || this.buildingPlan.name;

        try {
            const fragmentPath = this.buildingPlan?.attachment?.filePath;
            if (!fragmentPath) throw new Error('No fragment file path found');

            const signedUrl = await this.attachmentSrv.getSignedS3UrlForAttachmentFilePath(fragmentPath);
            const response = await fetch(signedUrl);
            if (!response.ok) throw new Error(`Failed to fetch fragments: ${response.status}`);

            const buffer = new Uint8Array(await response.arrayBuffer());
            // Pass the camera at load time so culling/LOD is active while the model is being built,
            // instead of materializing the full-detail model first (lower peak memory on iOS).
            this.loadedModel = await this.fragments.core.load(buffer, { modelId: planId, camera: this.world.camera.three });
            this.loadedModelId = planId;

            // Component was closed while the model was still loading: clean up silently
            if (this._destroyed) {
                this.fragments.core.disposeModel(planId).catch(() => {});
                return;
            }

            // Classification is non-fatal: the model is already loaded and visible.
            // If it fails, only the object-type toggle list is unavailable.
            try {
                await this.classifier.byCategory();
                const categories = this.classifier.list.get('Categories');
                if (categories) {
                    // Hide camera-marker proxies exported by Metaroom
                    const proxies = await this.classifier.find({ Categories: ['IFCBUILDINGELEMENTPROXY'] });
                    await this.hider.set(false, proxies);

                    this.ifcTypes = [...categories.keys()].map((name) => ({
                        name,
                        visible: name !== 'IFCBUILDINGELEMENTPROXY',
                    }));
                    this.cdr.detectChanges();
                }
            } catch (classifyErr) {
                this.logger.error('[BIM BUILDING PLAN] Classification failed (non-fatal):', classifyErr);
            }
        } catch (e) {
            this.logger.error('[BIM BUILDING PLAN] Failed to load fragments from S3:', e);
            if (!this._destroyed) {
                this.notifySrv.error(this.translate.instant('projects.plans.errors.load-error'));
                this.selectedFileName = '';
            }
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    /**
     * removes old building plan and builds new one
     */
    private async reloadPlan() {
        if (this.bpReloading) {
            this._pendingReload = true;
            return;
        }
        this.bpReloading = true;

        try {
            this.markerData.clear();
            this.jobToInstance.clear();
            for (const label of this.markerLabels.values()) {
                this.world?.scene?.three?.remove(label);
                (label.material as THREE.SpriteMaterial).map?.dispose();
                label.material.dispose();
            }
            this.markerLabels.clear();
            this.disposeMarkerMesh(this.markerHeadMesh);
            this.disposeMarkerMesh(this.markerNeedleMesh);
            this.markerCount = 0;
            this.loadedModel = null;
            this.modelLoaded = false;
            this.popupVisible = false;
            this.changedMarkers.clear();
            this.planDataChanged = false;
            this.unsavedChanges = false;
            this.ifcTypes = [];
            this.cdr.detectChanges();

            this.world?.camera?.controls?.removeEventListener('update', this.cameraUpdateListener);
            this.containerRef.nativeElement.removeEventListener('mousemove', this.markerMoveListener);
            this.containerRef.nativeElement.removeEventListener('click', this.markerClickListener);
            this.containerRef.nativeElement.removeEventListener('click', this.markingClickListener);
            await this.teardownScene();
            if (this._destroyed) return;

            await this.initScene();
            // If the component got destroyed while the new scene was being built,
            // dispose the freshly created scene too — ngOnDestroy already ran and can't know it.
            if (this._destroyed) {
                await this.teardownScene();
                return;
            }
            if (this._pendingReload) return;

            await this.loadFragmentsFromS3();
            if (this._destroyed) {
                await this.teardownScene();
                return;
            }
            if (this._pendingReload) return;

            this.initMarkerMesh();
            this.initMarkerClick();
            this.allMeasurements = null;
            this.initMeasurements();
            this.measurementMode = 'none';
            this.initMarking();

            const zoomedToMarker = await this.loadExistingMarkers();
            if (!zoomedToMarker) this.fitModel();
        } finally {
            this.bpReloading = false;
            if (this._pendingReload) {
                this._pendingReload = false;
                await new Promise((resolve) => setTimeout(resolve, 50));
                this.reloadPlan();
            }
        }
    }

    /**
     * Creates the marker pink
     */
    initMarkerMesh() {
        const headGeo = new THREE.SphereGeometry(0.05, 16, 16);
        headGeo.translate(0, 0.2, 0);
        this.markerHeadGeo = headGeo;

        const shaftGeo = new THREE.CylinderGeometry(0.012, 0.012, 0.15, 12);
        shaftGeo.translate(0, 0.095, 0);
        const tipGeo = new THREE.ConeGeometry(0.012, 0.06, 8);
        tipGeo.rotateX(Math.PI);
        tipGeo.translate(0, 0.025, 0);
        // Merge shaft and tip so the needle is one draw call instead of two
        this.markerNeedleGeo = BufferGeometryUtils.mergeGeometries([shaftGeo, tipGeo]);
        shaftGeo.dispose();
        tipGeo.dispose();

        this.buildMarkerMesh(this.markerCapacity);
    }

    /**
     * Builds a new InstancedMesh pair
     */
    private buildMarkerMesh(capacity: number) {
        const headMat = new THREE.MeshBasicMaterial({ vertexColors: false });
        const newHead = new THREE.InstancedMesh(this.markerHeadGeo, headMat, capacity);
        newHead.count = 0;
        newHead.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
        newHead.frustumCulled = false;

        const needleMat = new THREE.MeshBasicMaterial({ color: 0x888888 });
        const newNeedle = new THREE.InstancedMesh(this.markerNeedleGeo, needleMat, capacity);
        newNeedle.count = 0;
        newNeedle.frustumCulled = false;

        if (this.markerHeadMesh && this.markerCount > 0) {
            this.copyMarkerData(newHead, newNeedle);
            this.world.scene.three.remove(this.markerHeadMesh, this.markerNeedleMesh);
            this.disposeMarkerMesh(this.markerHeadMesh);
            this.disposeMarkerMesh(this.markerNeedleMesh);
        }

        this.markerHeadMesh = newHead;
        this.markerNeedleMesh = newNeedle;
        this.markerCapacity = capacity;
        this.world.scene.three.add(newHead, newNeedle);
    }

    /** Disposes an InstancedMesh and its material */
    private disposeMarkerMesh(mesh: THREE.InstancedMesh | undefined) {
        if (!mesh) return;
        if (!Array.isArray(mesh.material)) {
            mesh.material.dispose();
        } else {
            mesh.material.forEach((m) => m.dispose());
        }
        mesh.dispose();
    }

    /**
     * Copies InstancedMeshes into new one when old one is full and new ones created
     */
    private copyMarkerData(newHead: THREE.InstancedMesh, newNeedle: THREE.InstancedMesh) {
        const count = this.markerCount;
        const srcMatrix = this.markerHeadMesh.instanceMatrix.array as Float32Array;
        const srcColor = this.markerHeadMesh.instanceColor!.array as Float32Array;

        (newHead.instanceMatrix.array as Float32Array).set(srcMatrix.subarray(0, count * 16));
        (newHead.instanceColor!.array as Float32Array).set(srcColor.subarray(0, count * 3));
        (newNeedle.instanceMatrix.array as Float32Array).set(srcMatrix.subarray(0, count * 16));

        newHead.count = newNeedle.count = count;
        newHead.instanceMatrix.needsUpdate = true;
        newHead.instanceColor!.needsUpdate = true;
        newNeedle.instanceMatrix.needsUpdate = true;
    }

    /**
     * Grows the InstancedMesh capacity by 200 when all current slots are full
     */
    private growMarkerMesh() {
        this.buildMarkerMesh(this.markerCapacity + 200);
    }

    /**
     * Adds a pin marker at the given position for the given job and adds text label above the pin
     */
    onAddMarker(position: THREE.Vector3, jobId: string, color: string, label?: string) {
        if (!this.markerHeadMesh?.geometry || !this.markerNeedleMesh?.geometry) return;
        if (this.markerCount >= this.markerCapacity) this.growMarkerMesh();

        const index = this.markerCount++;
        this.threeMatrix.identity().setPosition(position);

        this.markerHeadMesh.setMatrixAt(index, this.threeMatrix);
        this.markerHeadMesh.setColorAt(index, this.threeColor.set(color));
        this.markerNeedleMesh.setMatrixAt(index, this.threeMatrix);

        this.markerData.set(index, jobId);
        this.jobToInstance.set(jobId, index);

        this.markerHeadMesh.count = this.markerCount;
        this.markerNeedleMesh.count = this.markerCount;
        this.markerHeadMesh.instanceMatrix.needsUpdate = true;
        this.markerHeadMesh.instanceColor!.needsUpdate = true;
        this.markerNeedleMesh.instanceMatrix.needsUpdate = true;
        this.markerHeadMesh.computeBoundingSphere();
        this.markerNeedleMesh.computeBoundingSphere();

        if (label) this.addMarkerLabel(jobId, position, label);
    }

    /**
     * Removes a marker instance by swapping it with the last active instance
     * Also removes the label and optionally deletes the job.
     */
    onRemoveMarker() {
        let prompt;
        if (this.projectView && this.settingsSrv.currentUserRights.canManageProjects) {
            prompt = this.notifySrv.tripleConfirm(
                this.translate.instant('projects.plans.info.delete-all-desc'),
                this.translate.instant('projects.plans.info.delete-all-title'),
                this.translate.instant('projects.plans.info.delete-all-confirm'),
                this.translate.instant('projects.plans.info.delete-all-deny'),
                this.translate.instant('shared.cancel'),
                true,
                true
            );
        } else {
            prompt = this.notifySrv.confirm(
                this.translate.instant('projects.plans.info.delete-marker-desc'),
                this.translate.instant('projects.plans.info.delete-marker-title'),
                this.translate.instant('shared.delete'),
                this.translate.instant('shared.cancel'),
                true
            );
        }

        prompt
            .then((onlyMarker: boolean) => {
                const index = this.jobToInstance.get(this.currentKey);
                if (index == null) return;

                const lastIndex = this.markerCount - 1;

                if (index !== lastIndex) {
                    // swap the last marker into the removed slot to avoid empty slots
                    this.markerHeadMesh.getMatrixAt(lastIndex, this.threeMatrix);
                    this.markerHeadMesh.setMatrixAt(index, this.threeMatrix);
                    this.markerNeedleMesh.setMatrixAt(index, this.threeMatrix);

                    this.markerHeadMesh.getColorAt(lastIndex, this.threeColor);
                    this.markerHeadMesh.setColorAt(index, this.threeColor);

                    const movedJobId = this.markerData.get(lastIndex)!;
                    this.markerData.set(index, movedJobId);
                    this.jobToInstance.set(movedJobId, index);
                }

                this.markerData.delete(lastIndex);
                this.jobToInstance.delete(this.currentKey);
                this.markerCount--;

                this.disposeLabel(this.currentKey);

                this.markerHeadMesh.count = this.markerNeedleMesh.count = this.markerCount;
                this.markerHeadMesh.instanceMatrix.needsUpdate = true;
                this.markerHeadMesh.instanceColor!.needsUpdate = true;
                this.markerNeedleMesh.instanceMatrix.needsUpdate = true;
                this.popupVisible = false;
                this.changedMarkers.delete(this.currentKey);

                if (onlyMarker) {
                    const [, deletedMarkerId] = this.currentKey.split('#');
                    this.db.getItemById(this.currentJobId).subscribe(
                        (jobData) => {
                            const job = new Job(jobData);
                            if (deletedMarkerId && job.marker?.length > 1) {
                                job.marker = job.marker.filter((m) => m._id !== deletedMarkerId);
                            } else {
                                job.buildingPlanId = '';
                                if (job.marker?.length > 0) job.marker[0].bimData = null;
                            }
                            this.db.put(job).subscribe(
                                () => this.logger.log('[BIM BUILDING PLAN] Removed marker from job', this.currentJobId),
                                (err) => this.logger.error('[BIM BUILDING PLAN] Failed to remove marker from job', err)
                            );
                        },
                        (err) => this.logger.error('[BIM BUILDING PLAN] Failed to load job for marker removal', err)
                    );
                } else {
                    this.db.delete(this.currentJobId).subscribe(
                        () => this.logger.log('[BIM BUILDING PLAN] Deleted job', this.currentJobId),
                        (err) => {
                            this.logger.error('[BIM BUILDING PLAN] Failed to delete job', err);
                            this.notifySrv.error(this.translate.instant('projects.plans.errors.delete-job-err'));
                        }
                    );
                }
            })
            .catch(() => this.logger.log('[BIM BUILDING PLAN] Cancelled marker deletion'));
    }

    /**
     * Handles color and label update
     */
    onJobMarkerChanged(event: { job: any; marker: any }) {
        const { job, marker } = event;
        const MarkerKey = `${job._id}#${marker._id}`;

        // update color
        const index = this.jobToInstance.get(MarkerKey);
        if (index == null) return;
        this.markerHeadMesh.setColorAt(index, this.threeColor.set(marker.color));
        this.markerHeadMesh.instanceColor!.needsUpdate = true;
        const existing = this.changedMarkers.get(MarkerKey) || {};
        existing.color = marker.color;
        this.changedMarkers.set(MarkerKey, existing);
        this.unsavedChanges = true;

        // update label
        this.markerHeadMesh.getMatrixAt(index, this.threeMatrix);
        this.threeVec3.setFromMatrixPosition(this.threeMatrix);
        this.addMarkerLabel(MarkerKey, this.threeVec3, marker.markerName || job.jobNumber);

        existing.markerName = marker.markerName;
        this.changedMarkers.set(MarkerKey, existing);

        // Sync changes back to @Input job so parent (job-details) picks them up on save
        if (this.job) {
            const inputMarker = this.job.marker?.find((m) => m._id === marker._id);
            if (inputMarker) {
                if (marker.color != null) inputMarker.color = marker.color;
                if (marker.markerName != null) inputMarker.markerName = marker.markerName;
            }
        }
    }

    /**
     * Renders a text label and places it above the pin head. Replaces existing label for the same job.
     */
    private addMarkerLabel(jobId: string, position: THREE.Vector3, text: string) {
        this.disposeLabel(jobId);

        const sprite = this.createLabel(text);
        sprite.position.set(position.x + 0.08, position.y + 0.22, position.z);
        this.world.scene.three.add(sprite);
        this.markerLabels.set(jobId, sprite);
    }

    /** Creates a rounded-rectangle label */
    private createLabel(text: string): THREE.Sprite {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        const fontSize = 25;
        const padding = 10;
        const radius = 8;

        ctx.font = `bold ${fontSize}px Quicksand`;
        const textWidth = ctx.measureText(text).width;
        canvas.width = textWidth + padding * 2;
        canvas.height = fontSize + padding * 2;

        ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.beginPath();
        ctx.roundRect(0, 0, canvas.width, canvas.height, radius);
        ctx.fill();

        ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.roundRect(0, 0, canvas.width, canvas.height, radius);
        ctx.stroke();

        ctx.font = `bold ${fontSize}px Quicksand`;
        ctx.fillStyle = '#000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        const material = new THREE.SpriteMaterial({ map: texture, depthTest: true });
        const sprite = new THREE.Sprite(material);

        const aspect = canvas.width / canvas.height;
        const height = 0.08;
        sprite.scale.set(height * aspect, height, 1);
        return sprite;
    }

    /** Removes and disposes the label for a given jobId, if one exists. */
    private disposeLabel(jobId: string) {
        const label = this.markerLabels.get(jobId);
        if (!label) return;
        this.world.scene.three.remove(label);
        try {
            (label.material as THREE.SpriteMaterial).map?.dispose();
            label.material.dispose();
        } catch (_) {
            /* already disposed after scene rebuild */
        }
        this.markerLabels.delete(jobId);
    }

    /**
     * Mouse/touch events give us 2D screen coordinates. To determine which marker
     * was clicked, we cast a ray from the camera into the 3D scene using THREE.Raycaster.
     */
    private raycastMarkers(event: MouseEvent, canvas: HTMLCanvasElement): THREE.Intersection[] {
        if (this.markerCount === 0) return [];
        const rect = canvas.getBoundingClientRect();
        this.rayMouse.set(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1);
        this.raycaster.setFromCamera(this.rayMouse, this.world.camera.three);

        return [...this.raycaster.intersectObject(this.markerHeadMesh), ...this.raycaster.intersectObject(this.markerNeedleMesh)].sort(
            (a, b) => a.distance - b.distance
        );
    }

    /**
     * Registers mousemove and click listeners for markers
     */
    initMarkerClick() {
        const canvas = this.world.renderer.three.domElement;

        this.markerMoveListener = (event: MouseEvent) => {
            if (this.markerMode) return;
            canvas.style.cursor = this.raycastMarkers(event, canvas).length > 0 ? 'pointer' : '';
        };

        this.markerClickListener = async (event: MouseEvent) => {
            if (this.measurementMode == 'area') {
                await this.areaMeasurer.create();
                let areas: number[] = [];
                let areaSum = 0;
                for (const area of this.areaMeasurer.list) {
                    areas.push(area.value);
                    areaSum += area.value;
                }
                this.allMeasurements = { unit: 'm²', values: areas, sum: this.roundTo2(areaSum) };
                return;
            } else if (this.measurementMode == 'length') {
                await this.lengthMeasurer.create();
                let lengths: number[] = [];
                let lengthSum = 0;
                for (const line of this.lengthMeasurer.list) {
                    lengths.push(line.value);
                    lengthSum += line.value;
                }
                this.allMeasurements = { unit: 'm', values: lengths, sum: this.roundTo2(lengthSum) };
                return;
            }
            if (this.markerMode) return;
            const hits = this.raycastMarkers(event, canvas);
            if (hits.length > 0) {
                const jobId = this.markerData.get(hits[0].instanceId);
                if (jobId) this.onMarkerClicked(jobId);
            } else if (this.popupVisible) {
                this.popupVisible = false;
                this.cdr.detectChanges();
            }
        };

        this.containerRef.nativeElement.addEventListener('mousemove', this.markerMoveListener);
        this.containerRef.nativeElement.addEventListener('click', this.markerClickListener);
    }

    private roundTo2(val: number): number {
        return Math.round(val * 100) / 100;
    }

    removeMeasurement(unit: string, index: number) {
        if (unit === 'm') {
            const items = [...this.lengthMeasurer.list];
            const item = items[index];
            if (item) this.lengthMeasurer.list.delete(item);

            const lengths: number[] = [];
            let sum = 0;
            for (const line of this.lengthMeasurer.list) {
                lengths.push(line.value);
                sum += line.value;
            }
            this.allMeasurements = { unit: 'm', values: lengths, sum: this.roundTo2(sum) };
        } else if (unit === 'm²') {
            const items = [...this.areaMeasurer.list];
            const item = items[index];
            if (item) this.areaMeasurer.list.delete(item);

            const areas: number[] = [];
            let sum = 0;
            for (const area of this.areaMeasurer.list) {
                areas.push(area.value);
                sum += area.value;
            }
            this.allMeasurements = { unit: 'm²', values: areas, sum: this.roundTo2(sum) };
        }
        this.cdr.detectChanges();
    }

    copyMeasurement(measurement) {
        navigator.clipboard.writeText(measurement).then(() => {
            this.notifySrv.success(this.translate.instant('projects.plans.bim.copy-successful'));
        });
    }

    /**
     * Registers a click listener for placing new markers
     */
    initMarking() {
        const raycasterComponent = this.components.get(OBC.Raycasters);
        const raycaster = raycasterComponent.get(this.world);

        this.markingClickListener = async (e: MouseEvent) => {
            if (this.addingNewMarkerToJob) {
                const result = await raycaster.castRay();
                if (result?.point) this.placeJobMarker(result);
                return;
            }

            if (!this.markerMode) return;

            const result = await raycaster.castRay();
            if (!result?.point) return;

            const availableServiceGroups = this.projectServiceGroups.filter(
                (sg) =>
                    sg.settings.dataRecording.locationRecording !== DataRecordingSetting.Disabled &&
                    this.projectSvc.serviceGroupChangesAllowedMap.get(sg._id)
            );

            if (availableServiceGroups.length === 0) {
                this.notifySrv.error(
                    this.translate.instant('projects.plans.errors.no-sg-desc'),
                    this.translate.instant('projects.plans.errors.no-sg-title')
                );
                this.utils.sentryCaptureMessage('No service groups for adding new item from map');
                return;
            }

            if (availableServiceGroups.length === 1) {
                this.createNewJobAndMarker(availableServiceGroups[0], result);
            } else {
                await this.pickServiceGroupAndCreateMarker(availableServiceGroups, result, e);
            }
        };

        this.containerRef.nativeElement.addEventListener('click', this.markingClickListener);
    }

    /**
     * Shows ActionSheet/Popover to let the user choose the service group of the new job
     */
    private async pickServiceGroupAndCreateMarker(groups: ServiceGroup[], rayResult: any, event: MouseEvent) {
        if (this.utils.isPhone) {
            const actionSheet = await this.actionSheetController.create({
                header: this.translate.instant('projects.plans.choose-article'),
                buttons: [
                    ...groups.map((sg) => ({
                        text: sg.name,
                        handler: () => this.createNewJobAndMarker(sg, rayResult),
                    })),
                    { text: this.translate.instant('shared.cancel'), role: 'cancel' },
                ],
            });
            await actionSheet.present();
        } else {
            const origBoundFunc = (event.target as HTMLElement).getBoundingClientRect;
            (event.target as HTMLElement).getBoundingClientRect = function () {
                return new DOMRect(event.clientX, event.clientY, 1, 1);
            };
            const pop = await this.popoverCtrl.create({
                component: ServiceGroupListComponent,
                componentProps: { items: groups },
                cssClass: 'dp-service-group-list',
                translucent: true,
                event,
            });
            await pop.present();
            (event.target as HTMLElement).getBoundingClientRect = origBoundFunc;
            const { data } = await pop.onDidDismiss();
            if (data?.selected) this.createNewJobAndMarker(data.selected, rayResult);
        }
    }

    /**
     * Creates a new Job in the DB and places a visual marker pin at the 3d position
     */
    private createNewJobAndMarker(sg: ServiceGroup, rayResult: any) {
        this.projectSvc.createNewJob(sg).subscribe(
            (job) => {
                job.marker = [new JobMarker()];
                job.marker[0].color = sg.settings.dataRecording.locationMarkerColor || '#e20a0a';
                job.buildingPlanId = this.buildingPlan._id;
                job.marker[0].bimData = new BimData({
                    position3d: { x: rayResult.point.x, y: rayResult.point.y, z: rayResult.point.z },
                    localId: rayResult.localId,
                    globalId: null,
                });

                if (rayResult.localId != null && this.loadedModel) {
                    this.loadedModel
                        .getGuidsByLocalIds([rayResult.localId])
                        .then((guids) => {
                            job.marker[0].bimData.globalId = guids[0] ?? null;
                            this.saveJob(job);
                        })
                        .catch(() => this.saveJob(job));
                } else {
                    this.saveJob(job);
                }

                this.onAddMarker(
                    this.threeVec3.set(rayResult.point.x, rayResult.point.y, rayResult.point.z),
                    `${job._id}#${job.marker[0]._id}`,
                    job.marker[0].color,
                    job.jobNumber
                );
                this.markerMode = false;
                this.addingNewItem = false;
                this.cdr.detectChanges();
            },
            (err) => {
                this.logger.error('[BIM] Could not create job', err);
                this.notifySrv.error(this.translate.instant('projects.plans.errors.create-job-err'));
            }
        );
    }

    /**
     * Places or updates the marker for the currently open job
     */
    private placeJobMarker(rayResult: any) {
        if (!this.job) return;

        const label = this.job.marker?.[0]?.markerName || this.job.jobNumber;
        const color = this.job.marker?.[0]?.color || '#e20a0a';

        if (this.allowMultipleMarker) {
            const newMarker = new JobMarker();
            newMarker.color = color;
            newMarker.bimData = new BimData({
                position3d: { x: rayResult.point.x, y: rayResult.point.y, z: rayResult.point.z },
                localId: rayResult.localId ?? null,
                globalId: null,
            });

            if (!this.job.marker) this.job.marker = [];
            this.job.marker.push(newMarker);

            this.onAddMarker(
                this.threeVec3.set(rayResult.point.x, rayResult.point.y, rayResult.point.z),
                `${this.job._id}#${newMarker._id}`,
                color,
                label
            );

            if (rayResult.localId != null && this.loadedModel) {
                this.loadedModel
                    .getGuidsByLocalIds([rayResult.localId])
                    .then((guids) => {
                        newMarker.bimData.globalId = guids[0] ?? null;
                        this.markerChanged.emit(this.job);
                    })
                    .catch(() => this.markerChanged.emit(this.job));
            } else {
                this.markerChanged.emit(this.job);
            }

            this.addingNewMarkerToJob = false;
        } else {
            if (!this.job.marker?.length) this.job.marker = [new JobMarker()];
            const compositeKey = `${this.job._id}#${this.job.marker[0]._id}`;
            const existingIndex = this.jobToInstance.get(compositeKey);
            if (existingIndex != null) {
                this.threeMatrix.identity().setPosition(rayResult.point);
                this.markerHeadMesh.setMatrixAt(existingIndex, this.threeMatrix);
                this.markerNeedleMesh.setMatrixAt(existingIndex, this.threeMatrix);
                this.markerHeadMesh.instanceMatrix.needsUpdate = true;
                this.markerNeedleMesh.instanceMatrix.needsUpdate = true;
                this.addMarkerLabel(compositeKey, rayResult.point, label);
            } else {
                this.onAddMarker(this.threeVec3.set(rayResult.point.x, rayResult.point.y, rayResult.point.z), compositeKey, color, label);
            }

            this.job.marker[0].bimData = new BimData({
                position3d: { x: rayResult.point.x, y: rayResult.point.y, z: rayResult.point.z },
                localId: rayResult.localId ?? null,
                globalId: null,
            });
            this.job.buildingPlanId = this.buildingPlan._id;

            if (rayResult.localId != null && this.loadedModel) {
                this.loadedModel
                    .getGuidsByLocalIds([rayResult.localId])
                    .then((guids) => {
                        this.job.marker[0].bimData.globalId = guids[0] ?? null;
                        this.markerChanged.emit(this.job);
                    })
                    .catch(() => this.markerChanged.emit(this.job));
            } else {
                this.markerChanged.emit(this.job);
            }

            this.addingNewMarkerToJob = false;
        }

        this.cdr.detectChanges();
    }

    /**
     * fetches all jobs that have a 3D position on this plan and renders the pins. In job-detail mode only the single job marker is shown.
     */
    async loadExistingMarkers(): Promise<boolean> {
        if (!this.projectView) {
            if (!this.job) return false;
            for (const marker of this.job.marker) {
                if (!marker?.bimData?.position3d) continue;
                const { x, y, z } = marker.bimData.position3d;
                const markerId = `${this.job._id}#${marker._id}`;
                this.onAddMarker(this.threeVec3.set(x, y, z), markerId, marker.color || '#e20a0a', marker.markerName || this.job.jobNumber);
            }
            const firstMarker = this.job.marker.find((m) => m?.bimData?.position3d);
            if (firstMarker) {
                const { x, y, z } = firstMarker.bimData.position3d;
                const sphere = new THREE.Sphere(this.threeVec3.set(x, y, z), 3);
                await this.world.camera.controls.fitToSphere(sphere, true);
                this.cdr.detectChanges();
                return true;
            }

            this.cdr.detectChanges();
            return false;
        } else {
            this.projectSvc
                .getJobsWithBasicInfo({ projectId: this.buildingPlan.projectId, planId: this.buildingPlan._id, type: 'job' })
                .pipe(take(1))
                .subscribe((jobs) => {
                    for (const job of jobs) {
                        for (const marker of job.marker) {
                            if (!marker?.bimData?.position3d) continue;
                            const { x, y, z } = marker.bimData.position3d;
                            const markerId = `${job._id}#${marker._id}`;
                            this.onAddMarker(
                                this.threeVec3.set(x, y, z),
                                markerId,
                                marker.color || '#e20a0a',
                                marker.markerName || job.jobNumber
                            );
                        }
                    }
                    this.cdr.detectChanges();
                });
            return false;
        }
    }

    /**
     * On the marker pin click, load the full job and open the popup above pin
     */
    onMarkerClicked(id: string) {
        this.popupVisible = false;
        this.cdr.detectChanges();
        const [jobId, markerId] = id.split('#');
        this.currentJobId = jobId;
        this.currentKey = id;
        this.displayMarkerId = markerId || '';
        this.db.getItemById(jobId).subscribe((jobData) => {
            const job = new Job(jobData);
            this.displayServiceGroup = this.projectServiceGroups.find((s) => s._id === job.serviceGroupId);
            this.displayJob = job;
            this.displayProject = this.projectSvc.currentProjectData;

            const marker = markerId ? job.marker.find((m) => m._id === markerId) : job.marker[0];
            const pos = marker?.bimData?.position3d;
            if (pos) {
                const rect = this.world.renderer.three.domElement.getBoundingClientRect();
                const projected = this.threeVec3.set(pos.x, pos.y, pos.z).project(this.world.camera.three);
                let px = ((projected.x + 1) / 2) * rect.width;
                let py = ((-projected.y + 1) / 2) * rect.height;

                const popupW = Math.min(300, rect.width - 16);
                const pad = 8;
                px = Math.max(popupW / 2 + pad, Math.min(px, rect.width - popupW / 2 - pad));
                py = Math.max(270, Math.min(py, rect.height - pad));

                this.popupX = px;
                this.popupY = py;
            }
            this.popupVisible = true;
            this.cdr.detectChanges();
        });
    }

    /** Opens the full job detail view for the current job */
    onOpenJobDetails(sg: ServiceGroup) {
        this.jobShouldShow.emit({ job: this.displayJob, servicegroup: sg, project: this.projectSvc.currentProjectData });
    }

    /**
     * show a list of all existing ifc types
     */
    async onToggleIfcType(type: { name: string; visible: boolean }) {
        type.visible = !type.visible;
        const found = await this.classifier.find({ Categories: [type.name] });
        await this.hider.set(type.visible, found);
    }

    /**
     * Updates the element count when the user picks an IFC type
     */
    async onImportTypeChange(typeName: string) {
        this.selectedImportType = typeName;
        if (!typeName) {
            this.importElementCount = 0;
            return;
        }
        const found = await this.classifier.find({ Categories: [typeName] });
        this.importElementCount = found[this.buildingPlan._id]?.size ?? 0;
        this.cdr.detectChanges();
    }

    /**
     * creates a Job and adds a marker pin for every job to import
     */
    async generateJobs() {
        if (!this.selectedImportType || !this.selectedImportServiceGroup) return;

        const sg = this.selectedImportServiceGroup;
        const found = await this.classifier.find({ Categories: [this.selectedImportType] });
        const localIds = found[this.buildingPlan._id];
        if (!localIds?.size) return;

        this.generatingJobs = true;
        this.cdr.detectChanges();
        const localIdArray = [...localIds];

        const boxes = await this.loadedModel.getBoxes(localIdArray);
        const guids = await this.loadedModel.getGuidsByLocalIds(localIdArray);
        const color = sg.settings.dataRecording.locationMarkerColor || '#e20a0a';

        for (let i = 0; i < localIdArray.length; i++) {
            (boxes[i] as THREE.Box3).getCenter(this.threeVec3);
            this.threeVec3.y = (boxes[i] as THREE.Box3).max.y;

            const job = await this.projectSvc.createNewJob(sg).toPromise();
            const marker = new JobMarker();
            marker.color = color;
            marker.bimData = new BimData({
                position3d: { x: this.threeVec3.x, y: this.threeVec3.y, z: this.threeVec3.z },
                localId: localIdArray[i],
                globalId: guids[i] ?? null,
            });
            job.marker = [marker];
            job.buildingPlanId = this.buildingPlan._id;
            this.saveJob(job);
            this.onAddMarker(this.threeVec3, `${job._id}#${marker._id}`, color, job.jobNumber);
        }

        this.generatingJobs = false;
        this.activePopover = null;
        this.selectedImportType = null;
        this.selectedImportServiceGroup = null;
        this.importElementCount = 0;
        this.notifySrv.success(this.translate.instant('projects.plans.bim.successful-created-jobs', { jobcount: localIdArray.length }));
        this.cdr.detectChanges();
    }

    /**
     * Updates all changed markers and notifies the parent component of changes
     */
    onSaveBIMPlan() {
        if (this.changedMarkers.size > 0) {
            // Group changes by jobId so each job is loaded and saved only once
            const grouped = new Map<string, Array<{ markerId: string; changes: { color?: string; markerName?: string } }>>();
            for (const [compositeKey, changes] of this.changedMarkers.entries()) {
                const [jobId, markerId] = compositeKey.split('#');
                if (!grouped.has(jobId)) grouped.set(jobId, []);
                grouped.get(jobId).push({ markerId, changes });
            }

            const updates = [...grouped.entries()].map(
                ([jobId, markerChanges]) =>
                    new Promise<void>((resolve, reject) => {
                        this.db.getItemById(jobId).subscribe(
                            (jobData) => {
                                const job = new Job(jobData);
                                for (const { markerId, changes } of markerChanges) {
                                    const marker = markerId ? job.marker?.find((m) => m._id === markerId) : job.marker?.[0];
                                    if (marker) {
                                        if (changes.color != null) marker.color = changes.color;
                                        if (changes.markerName != null) marker.markerName = changes.markerName;
                                    }
                                }
                                this.db.put(job).subscribe(
                                    () => {
                                        this.logger.log('[BIM] Saved marker changes for job', jobId);
                                        resolve();
                                    },
                                    (err) => {
                                        this.logger.error('[BIM] Failed to save marker changes', err);
                                        reject(err);
                                    }
                                );
                            },
                            (err) => reject(err)
                        );
                    })
            );

            Promise.all(updates)
                .then(() => {
                    this.notifySrv.success(this.translate.instant('projects.plans.info.saved-markers'));
                    this.changedMarkers.clear();
                })
                .catch((err) => {
                    this.logger.error('[BIM] Failed saving markers', err);
                    this.notifySrv.error(this.translate.instant('projects.plans.errors.save-markers-err'));
                });
        }

        this.successClicked.emit(this.planDataChanged);
        this.planDataChanged = false;
        this.unsavedChanges = false;
    }

    /** saves the job */
    private saveJob(job: Job) {
        this.db.put(job).subscribe(
            () => this.logger.log('[BIM] Job saved with bimData', job._id),
            (err) => this.logger.error('[BIM] Failed to save job', err)
        );
    }

    /**
     * Zooms the camera to fit the whole model
     */
    fitModel() {
        const boxer = this.components.get(OBC.BoundingBoxer);
        boxer.list.clear();
        boxer.addFromModels();

        const box = boxer.get();
        const sphere = new THREE.Sphere();
        box.getBoundingSphere(sphere);
        this.world.camera.controls.fitToSphere(sphere, true);
        boxer.list.clear();
    }

    /**
     * Moves the camera in the given direction. Used by both keyboard controls and mobile navigation buttons.
     */
    moveCamera(direction: 'up' | 'down' | 'left' | 'right' | 'zoom-in' | 'zoom-out') {
        const speed = 1;
        switch (direction) {
            case 'up':
                this.world.camera.controls.forward(speed, true);
                break;
            case 'down':
                this.world.camera.controls.forward(-speed, true);
                break;
            case 'left':
                this.world.camera.controls.truck(-speed, 0, true);
                break;
            case 'right':
                this.world.camera.controls.truck(speed, 0, true);
                break;
        }
    }

    zoomModel(direction: 'in' | 'out') {
        const controls = this.world.camera.controls;
        const factor = direction === 'in' ? 1.3 : 1 / 1.3;
        controls.zoomTo(this.world.camera.three.zoom * factor, true);
    }

    /**
     * Registers arrow-key navigation to move the camera
     */
    initKeyboardControls() {
        const keyMap: Record<string, 'up' | 'down' | 'left' | 'right'> = {
            ArrowUp: 'up',
            ArrowDown: 'down',
            ArrowLeft: 'left',
            ArrowRight: 'right',
        };
        this.keydownListener = (e: KeyboardEvent) => {
            const dir = keyMap[e.key];
            if (dir) this.moveCamera(dir);
        };
        window.addEventListener('keydown', this.keydownListener);
    }

    /** Toggles the split-pane side menu and refits the model to the new size */
    onToggleSplitPaneMenu() {
        this.utils.toggleSplitPaneMenu('building-plan-split-pane');
        this.fitModel();
    }

    /** Prompts the user to rename the current plan */
    async onReplacePlanName() {
        try {
            const newName = (await this.notifySrv.prompt(
                undefined,
                this.translate.instant('projects.plans.info.rename-plan-title'),
                this.buildingPlan.name,
                this.translate.instant('projects.plans.info.rename-plan-placeholder'),
                'half-width-modal'
            )) as string;
            if (newName && newName !== this.buildingPlan.name) {
                this.planDataChanged = true;
                this.unsavedChanges = true;
                this.buildingPlan.name = newName;
                this.cdr.detectChanges();
            }
        } catch (_) {
            // user cancelled
        }
    }

    toggleMarkerMode() {
        this.markerMode = !this.markerMode;
        if (this.markerMode) {
            this.notifySrv.info(this.translate.instant('projects.plans.info.place-new-marker'));
            this.projectView ? (this.addingNewItem = true) : (this.addingNewMarkerToJob = true);
        } else {
            this.addingNewItem = false;
            this.addingNewMarkerToJob = false;
        }
    }

    toggleJobMarkerMode() {
        this.addingNewMarkerToJob = !this.addingNewMarkerToJob;
    }

    onPlanSelectChange(planId: string) {
        this.planChanged.emit(planId);
    }

    toggleBackdrop(open: boolean) {
        this.showBackdrop = open;
    }

    onCancelBtn() {
        this.successClicked.emit(false);
    }

    onDestructiveBtn() {
        this.destructiveClicked.emit();
    }

    openPopover(type: 'import' | 'objectTypes' | 'measurement', event: Event) {
        this.activePopover = type;
        this.activePopoverEvent = event;
    }

    /** Exports all markers of the plan as a BCF file and triggers a download */
    async exportMarkers() {
        this.runningExport = true;
        this.cdr.detectChanges();
        try {
            const jobs = await this.projectSvc.getJobs({ projectId: this.buildingPlan.projectId }).pipe(take(1)).toPromise();
            const planJobs = jobs.filter((j) => j.buildingPlanId === this.buildingPlan._id);
            await this.bimSrv.exportJobsAsBcf(planJobs, this.buildingPlan.name);
        } catch (err) {
            this.logger.error('[BIM BUILDING PLAN] BCF export failed', err);
            this.notifySrv.error(
                this.translate.instant('projects.plans.errors.export-err-desc'),
                this.translate.instant('projects.plans.errors.export-err-title')
            );
        } finally {
            this.runningExport = false;
            this.cdr.detectChanges();
        }
    }

    protected readonly navigator = navigator;
}
